import { CustomCard } from '@/components/custom-card';
import { SubheadingSm } from '@/components/custom-titles';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/numberFormat';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { PLANT_COLORS } from '../constants';
import { InsuranceContractByPlant } from '../model';

interface InsurancePlantDonutProps {
  data?: InsuranceContractByPlant[];
  isLoading?: boolean;
  className?: string;
}

/** Ekin turlari bo'yicha shartnomalar ulushi. */
export const InsurancePlantDonut = ({
  data,
  isLoading,
  className,
}: InsurancePlantDonutProps) => {
  const { t } = useTranslation();

  const rows = (data || []).filter((item) => item.contract_count > 0);
  const total = rows.reduce((sum, item) => sum + item.contract_count, 0);

  return (
    <CustomCard className={cn('flex w-full flex-col gap-4', className)}>
      <SubheadingSm title={t('insurance_by_plant')} />

      {isLoading ? (
        <div className="flex h-[320px] items-center justify-center">
          <div className="progress" />
        </div>
      ) : rows.length ? (
        <div className="flex flex-1 flex-col gap-4">
          <div className="relative min-h-[240px] flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rows}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={1}
                  dataKey="contract_count"
                  startAngle={90}
                  endAngle={450}
                  stroke="none"
                >
                  {rows.map((row, index) => (
                    <Cell
                      key={row.plant_id}
                      fill={PLANT_COLORS[index % PLANT_COLORS.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Markazdagi umumiy son — diagramma ustida turadi. */}
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-semibold text-foreground">
                {formatNumber(total)}
              </span>
              <span className="text-xs font-normal text-foreground opacity-50">
                {t('ta')} {t('contracts').toLowerCase()}
              </span>
            </div>
          </div>

          <div className="mt-auto flex flex-col gap-2">
            {rows.map((row, index) => (
              <div
                key={row.plant_id}
                className="flex items-center justify-between gap-3"
              >
                <span className="flex items-center gap-2">
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{
                      backgroundColor:
                        PLANT_COLORS[index % PLANT_COLORS.length],
                    }}
                  />
                  <span className="text-xs font-normal text-foreground opacity-70">
                    {row.plant_name}
                  </span>
                </span>
                <span className="text-xs font-medium text-foreground">
                  {formatNumber(row.contract_count)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
          {t('no_data')}
        </div>
      )}
    </CustomCard>
  );
};
